import { state } from './state';
import { $maybe } from './ui';

let audioEl: HTMLAudioElement | null = null;
let lastActiveIdx = -1;
let shortcutsAttached = false;
let segmentEndTime: number | null = null;
let segmentBubbleIdx = -1;

function clearSegment(): void {
  segmentEndTime = null;
  segmentBubbleIdx = -1;
}

function findActiveParagraph(time: number): number {
  const ps = state.paragraphs;
  if (ps.length === 0) return -1;
  let idx = -1;
  for (let i = 0; i < ps.length; i++) {
    if (ps[i].start <= time) idx = i;
    else break;
  }
  return idx;
}

function clearActiveBubble(): void {
  for (const el of document.querySelectorAll('.bubble-playing')) {
    el.classList.remove('bubble-playing');
  }
}

function onTimeUpdate(): void {
  if (!audioEl) return;
  if (segmentEndTime != null && audioEl.currentTime >= segmentEndTime) {
    audioEl.pause();
    clearSegment();
  }
  const idx = findActiveParagraph(audioEl.currentTime);
  if (idx === lastActiveIdx) return;
  clearActiveBubble();
  if (idx >= 0) {
    const el = document.querySelector(`[data-bubble-index="${idx}"]`);
    if (el) {
      el.classList.add('bubble-playing');
      el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }
  lastActiveIdx = idx;
}

export function initPlayer(): void {
  destroyPlayer();
  const src = state.currentAudioUrl;
  const container = $maybe('audioPlayerContainer');
  if (!container) return;
  if (!src) {
    container.style.display = 'none';
    container.innerHTML = '';
    return;
  }

  audioEl = document.createElement('audio');
  audioEl.controls = true;
  audioEl.preload = 'metadata';
  audioEl.className = 'w-full';
  audioEl.src = src;

  audioEl.addEventListener('timeupdate', onTimeUpdate);
  audioEl.addEventListener('pause', clearSegment);
  audioEl.addEventListener('error', () => {
    container.style.display = 'none';
  });

  container.innerHTML = '';
  container.appendChild(audioEl);
  container.style.display = 'block';
  lastActiveIdx = -1;
}

export function destroyPlayer(): void {
  if (audioEl) {
    audioEl.pause();
    audioEl.removeEventListener('timeupdate', onTimeUpdate);
    audioEl.removeEventListener('pause', clearSegment);
    audioEl.src = '';
    audioEl = null;
  }
  const container = $maybe('audioPlayerContainer');
  if (container) {
    container.innerHTML = '';
    container.style.display = 'none';
  }
  clearActiveBubble();
  clearSegment();
  lastActiveIdx = -1;
}

export function playSegment(index: number, start: number, end: number | null | undefined): void {
  if (!audioEl) return;
  if (segmentBubbleIdx === index && !audioEl.paused) {
    audioEl.pause();
    return;
  }
  segmentBubbleIdx = index;
  segmentEndTime = typeof end === 'number' && Number.isFinite(end) ? end : null;
  audioEl.currentTime = start;
  void audioEl.play();
}

export function seekTo(seconds: number, autoPlay = true): void {
  if (!audioEl) return;
  audioEl.currentTime = seconds;
  if (autoPlay) void audioEl.play();
}

export function togglePlay(): void {
  if (!audioEl) return;
  if (audioEl.paused) void audioEl.play();
  else audioEl.pause();
}

export function toggleAtBubble(index: number, seconds: number): void {
  if (!audioEl) {
    return;
  }
  if (lastActiveIdx === index && !audioEl.paused) {
    audioEl.pause();
    return;
  }
  audioEl.currentTime = seconds;
  void audioEl.play();
}

function jumpToParagraph(direction: 'prev' | 'next'): void {
  if (!audioEl) return;
  const ps = state.paragraphs;
  if (ps.length === 0) return;
  const currentIdx = findActiveParagraph(audioEl.currentTime);
  let target: number;
  if (direction === 'next') {
    target = Math.min(ps.length - 1, currentIdx + 1);
  } else {
    const elapsed = audioEl.currentTime - (ps[currentIdx]?.start ?? 0);
    target = elapsed > 2 || currentIdx <= 0 ? Math.max(0, currentIdx) : currentIdx - 1;
  }
  const wasPlaying = !audioEl.paused;
  seekTo(ps[target].start, wasPlaying);
}

function isTypingTarget(el: EventTarget | null): boolean {
  if (!(el instanceof HTMLElement)) return false;
  if (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA') return true;
  if (el.isContentEditable) return true;
  return false;
}

export function setupPlayerShortcuts(): void {
  if (shortcutsAttached) return;
  shortcutsAttached = true;
  document.addEventListener('keydown', (e) => {
    if (!audioEl) return;
    if (isTypingTarget(e.target)) return;

    if (e.code === 'Space') {
      e.preventDefault();
      togglePlay();
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      jumpToParagraph('prev');
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      jumpToParagraph('next');
    }
  });
}
